import {Color} from '@benchling/nucleus-ui';
import {createStyles, Text} from '@mantine/core';
import React from 'react';

import {
  ProcedureRunDataEntryMode,
  ProcedureType,
  StudyType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {StudyDesignTabEditState} from 'studies/typed/shared/procedure-runs/design-tabs/StudyDesignTabSavingButtons';
import MultiBatchSelector from 'studies/typed/shared/procedure-runs/multi-batch-selector/MultiBatchSelector';
import ProcedureCard from 'studies/typed/shared/procedure-runs/procedure-selection/ProcedureCard';
import {getTypedStudyAdapter} from 'studies/typed/TypedStudyAdapters';
import getTestProps from 'test/lib/cypress/getTestProps';

export interface PlanSettingsSectionProps {
  editState: StudyDesignTabEditState;
  procedureId: string;
  flowchartVersionId: string;
  procedureType: ProcedureType;
  studyType: StudyType;
  onRemoveProcedure: () => Promise<void>;
  hasUpdateAvailable: boolean;
  isProcedureVersionUpdateDisabled?: boolean;
  onOpenUpdateModal: () => void;
  dataEntryMode: ProcedureRunDataEntryMode;
  setDataEntryMode: (dataEntryMode: ProcedureRunDataEntryMode) => void;
}

export default function PlanSettingsSection(props: PlanSettingsSectionProps): JSX.Element {
  const {classes} = useStyles();
  const isEditable = props.editState === StudyDesignTabEditState.EDITING;
  const procedureString = getProcedureUIString(ProceduresStringKeys.PROCEDURE, props.procedureType, {
    stringCase: StringCase.LOWER,
  });

  return (
    <div {...getTestProps({component: 'PlanSettingsSection', element: 'root'})}>
      <Text className={classes.procedureCardLabel}>{`Active ${procedureString}`}</Text>
      <ProcedureCard
        procedureId={props.procedureId}
        flowchartVersionId={props.flowchartVersionId}
        procedureType={props.procedureType}
        studyType={props.studyType}
        onRemoveProcedure={props.onRemoveProcedure}
        isEditable={isEditable}
        hasUpdateAvailable={props.hasUpdateAvailable}
        isUpdateDisabled={props.isProcedureVersionUpdateDisabled ?? false}
        onOpenUpdateModal={props.onOpenUpdateModal}
      />
      {getTypedStudyAdapter(props.studyType).isMultiBatchEnabled && (
        <div className={classes.multiBatchSelectorWrapper}>
          <MultiBatchSelector
            dataEntryMode={props.dataEntryMode}
            onSelectDataEntryMode={props.setDataEntryMode}
            isDisabled={!isEditable}
            withContainerPadding={false}
          />
        </div>
      )}
    </div>
  );
}

const useStyles = createStyles((theme) => ({
  multiBatchSelectorWrapper: {
    marginTop: theme.spacing.xl,
  },
  procedureCardLabel: {
    color: Color.BLACK,
    fontSize: theme.fontSizes.sm,
    fontWeight: 600,
    lineHeight: '14px',
    marginBottom: theme.spacing.md,
    marginTop: theme.spacing.md,
  },
}));
